import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  TrendingUp as ProgressIcon,
  CheckCircle as CheckIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Share as ShareIcon
} from '@mui/icons-material';

const StudyPlanTemplate = ({ studyPlan, onEdit, onDownload, onShare }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <CalendarIcon className="w-4 h-4" /> },
    { id: 'schedule', label: 'Daily Schedule', icon: <ScheduleIcon className="w-4 h-4" /> },
    { id: 'progress', label: 'Progress', icon: <ProgressIcon className="w-4 h-4" /> },
    { id: 'goals', label: 'Goals', icon: <CheckIcon className="w-4 h-4" /> }
  ];

  const renderOverview = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <CalendarIcon className="w-6 h-6 text-blue-500" />
            <h3 className="font-semibold text-blue-800 dark:text-blue-200">Study Period</h3>
          </div>
          <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
            {studyPlan.duration || '4 weeks'}
          </p>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            {studyPlan.startDate} - {studyPlan.endDate}
          </p>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <ScheduleIcon className="w-6 h-6 text-green-500" />
            <h3 className="font-semibold text-green-800 dark:text-green-200">Study Hours</h3>
          </div>
          <p className="text-2xl font-bold text-green-900 dark:text-green-100">
            {studyPlan.totalHours || '120h'}
          </p>
          <p className="text-sm text-green-700 dark:text-green-300">
            {studyPlan.dailyHours || '4h'} per day
          </p>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-3">
            <ProgressIcon className="w-6 h-6 text-purple-500" />
            <h3 className="font-semibold text-purple-800 dark:text-purple-200">Subjects</h3>
          </div>
          <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
            {studyPlan.subjects?.length || 5}
          </p>
          <p className="text-sm text-purple-700 dark:text-purple-300">
            {studyPlan.subjects?.slice(0, 2).join(', ')}...
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
          Study Strategy
        </h3>
        <div className="space-y-4">
          {studyPlan.strategy?.map((item, index) => (
            <div key={index} className="flex items-start gap-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-gray-700 dark:text-gray-300">{item}</p>
            </div>
          )) || (
            <p className="text-gray-600 dark:text-gray-400 italic">
              Study strategy will be generated based on your routine and exam schedule.
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );

  const renderSchedule = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
            Weekly Study Schedule
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Time</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Monday</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Tuesday</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Wednesday</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Thursday</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Friday</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Saturday</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Sunday</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {studyPlan.schedule?.map((timeSlot, index) => (
                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-900/50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {timeSlot.time}
                  </td>
                  {timeSlot.days.map((day, dayIndex) => (
                    <td key={dayIndex} className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {day.subject && (
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${day.color || 'bg-blue-500'}`}></div>
                          <span className="font-medium">{day.subject}</span>
                        </div>
                      )}
                      {day.activity && (
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {day.activity}
                        </p>
                      )}
                    </td>
                  ))}
                </tr>
              )) || (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    Your personalized study schedule will be generated here.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );

  const renderProgress = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
            Overall Progress
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                <span>Study Plan Progress</span>
                <span>{studyPlan.progress?.overall || 0}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${studyPlan.progress?.overall || 0}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                <span>Daily Goals</span>
                <span>{studyPlan.progress?.daily || 0}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-green-500 to-blue-500 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${studyPlan.progress?.daily || 0}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
            Subject Progress
          </h3>
          <div className="space-y-3">
            {studyPlan.subjects?.map((subject, index) => (
              <div key={index}>
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                  <span>{subject.name || subject}</span>
                  <span>{subject.progress || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${subject.progress || 0}%` }}
                  ></div>
                </div>
              </div>
            )) || (
              <p className="text-gray-500 dark:text-gray-400 text-sm italic">
                Subject progress will be tracked as you study.
              </p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );

  const renderGoals = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
            Study Goals
          </h3>
          <div className="space-y-3">
            {studyPlan.goals?.map((goal, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                <CheckIcon className={`w-5 h-5 mt-0.5 ${goal.completed ? 'text-green-500' : 'text-gray-400'}`} />
                <div className="flex-1">
                  <p className={`font-medium ${goal.completed ? 'text-gray-600 dark:text-gray-400 line-through' : 'text-gray-800 dark:text-gray-200'}`}>
                    {goal.title}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {goal.description}
                  </p>
                  {goal.deadline && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      Due: {goal.deadline}
                    </p>
                  )}
                </div>
              </div>
            )) || (
              <p className="text-gray-500 dark:text-gray-400 text-sm italic">
                Study goals will be set based on your exam schedule.
              </p>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
            Milestones
          </h3>
          <div className="space-y-4">
            {studyPlan.milestones?.map((milestone, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${milestone.completed ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                <div className="flex-1">
                  <p className={`text-sm ${milestone.completed ? 'text-gray-600 dark:text-gray-400 line-through' : 'text-gray-800 dark:text-gray-200'}`}>
                    {milestone.title}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {milestone.date}
                  </p>
                </div>
              </div>
            )) || (
              <p className="text-gray-500 dark:text-gray-400 text-sm italic">
                Study milestones will be created based on your exam dates.
              </p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'schedule':
        return renderSchedule();
      case 'progress':
        return renderProgress();
      case 'goals':
        return renderGoals();
      default:
        return null;
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">
              Your Study Plan
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Personalized study schedule and progress tracking
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200"
            >
              <EditIcon className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={onDownload}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200"
            >
              <DownloadIcon className="w-4 h-4" />
              Download PDF
            </button>
            <button
              onClick={onShare}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200"
            >
              <ShareIcon className="w-4 h-4" />
              Share
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mt-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <AnimatePresence mode="wait">
          {renderTabContent()}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default StudyPlanTemplate; 